/**
 * Página de criar protocolo usando componentes FluxVet (form + TemplateSaver).
 * Usada quando !defaultApp: criar protocolo fora do atendimento com o mesmo UX do PrescriptionsStep.
 * Formulário local (treatments, exams, reports, guidelines) + TemplateSaver para persistir.
 */

import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Tab, Tabs, Button, Stack, Tooltip } from "@mui/material";
import {
  Add,
  Medication,
  Science,
  Description,
  InfoOutlined,
  Save,
  ArrowBack,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { VetAttendanceProvider } from "@/fluxvet/contexts/VetAttendanceContext";
import { TreatmentsSection } from "@/fluxvet/pages/vetattendence/components/TreatmentsSection";
import { ExamsProceduresSection } from "@/fluxvet/pages/vetattendence/components/ExamsProceduresSection";
import { ReportsSection } from "@/fluxvet/pages/vetattendence/components/ReportsSection";
import { GuidelinesSection } from "@/fluxvet/pages/vetattendence/components/GuidelinesSection";
import { TemplateSaver } from "@/fluxvet/pages/vetattendence/components/TemplateSaver";
import { LargeContentBox } from "../../components/common";
import { PageHeader } from "@/components/common/PageHeader";

/** Formulário apenas com os campos de prescrição (mesmo shape do PrescriptionsStep) */
interface ProtocolFormValues {
  treatments: Array<{
    id: string;
    name: string;
    dosage: string;
    description: string;
  }>;
  examsOrProcedures: Array<{
    id: string;
    description: string;
    notes: string;
  }>;
  reports: Array<{ id: string; content: string }>;
  guidelines: string;
}

const defaultValues: ProtocolFormValues = {
  treatments: [],
  examsOrProcedures: [],
  reports: [],
  guidelines: "",
};

function ProtocolCreatorForm() {
  const navigate = useNavigate();
  const methods = useForm<ProtocolFormValues>({
    mode: "onChange",
    defaultValues,
  });
  const { control, handleSubmit, watch, setValue } = methods;
  const [activeTab, setActiveTab] = useState(0);
  const [showTemplateSaver, setShowTemplateSaver] = useState(false);

  const treatments = watch("treatments") || [];
  const examsOrProcedures = watch("examsOrProcedures") || [];
  const reports = watch("reports") || [];
  const guidelines = watch("guidelines") || "";

  const hasItemsToSave =
    treatments.length > 0 ||
    examsOrProcedures.length > 0 ||
    reports.length > 0 ||
    !!guidelines;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  /** Ao salvar o protocolo: fecha o modal, exibe toast e volta para a página anterior. */
  const handleTemplateSaved = () => {
    setShowTemplateSaver(false);
    toast.success("Protocolo criado com sucesso!");
    navigate(-1);
  };

  const saveButtonProps = {
    color: "primary" as const,
    variant: "contained" as const,
    size: "large" as const,

    onClick: () => setShowTemplateSaver(true),
    disabled: !hasItemsToSave,
    sx: {
      textTransform: "none" as const,

      boxShadow: 2,

      "&.Mui-disabled": {
        opacity: 0.6,
      },
    },
  };

  const saveButton = !hasItemsToSave ? (
    <Tooltip title="Adicione ao menos um item (tratamento, exame, relatório ou orientação) para poder salvar o protocolo.">
      <span>
        <Button {...saveButtonProps}>Salvar </Button>
      </span>
    </Tooltip>
  ) : (
    <Button {...saveButtonProps}>Salvar</Button>
  );

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(() => {})}
        sx={{ width: "100%" }}
      >
        <PageHeader
          title="Criar protocolo"
          icon={Add}
          description="Adicione itens nas abas abaixo (tratamentos, exames, relatórios ou orientações) e clique em Salvar protocolo para criar."
          action={
            <Stack direction="row" gap={2}>
              {saveButton}
              <Button
                size="small"
                startIcon={<ArrowBack />}
                onClick={() => navigate(-1)}
                sx={{
                  padding: "10px 20px",
                  textTransform: "none",
                  color: "#666",
                  "&:hover": {
                    color: "#00A884",
                    bgcolor: "rgba(0, 168, 132, 0.08)",
                  },
                }}
              >
                Voltar
              </Button>
            </Stack>
          }
        />

        <Stack sx={{ mt: 3 }}>
          <Paper
            sx={{
              width: "100%",
              minHeight: 550,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTab-root": {
                  minHeight: 64,
                  textTransform: "none",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                },
                "& .Mui-selected": {
                  color: "#00A884 !important",
                  fontWeight: 600,
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#00A884",
                  height: 3,
                },
              }}
            >
              <Tab
                icon={<Medication />}
                iconPosition="start"
                label="Tratamentos"
              />
              <Tab
                icon={<Science />}
                iconPosition="start"
                label="Exames/Procedimentos"
              />
              <Tab
                icon={<Description />}
                iconPosition="start"
                label="Relatórios"
              />
              <Tab
                icon={<InfoOutlined />}
                iconPosition="start"
                label="Orientações"
              />
            </Tabs>

            <Box sx={{ p: 3, minHeight: 470 }}>
              {activeTab === 0 && (
                <TreatmentsSection
                  control={control}
                  watch={watch}
                  setValue={setValue}
                />
              )}
              {activeTab === 1 && (
                <ExamsProceduresSection
                  control={control}
                  watch={watch}
                  setValue={setValue}
                />
              )}
              {activeTab === 2 && (
                <ReportsSection
                  control={control}
                  watch={watch}
                  setValue={setValue}
                />
              )}
              {activeTab === 3 && <GuidelinesSection control={control} />}
            </Box>
          </Paper>
        </Stack>

        {showTemplateSaver && (
          <TemplateSaver
            open={showTemplateSaver}
            onClose={() => setShowTemplateSaver(false)}
            onSaveSuccess={handleTemplateSaved}
            treatments={treatments}
            examsOrProcedures={examsOrProcedures}
            reports={reports}
            guidelines={guidelines}
          />
        )}
      </Box>
    </FormProvider>
  );
}

export function ProtocolCreatorPageFluxvet() {
  return (
    <VetAttendanceProvider>
      <LargeContentBox>
        <ProtocolCreatorForm />
      </LargeContentBox>
    </VetAttendanceProvider>
  );
}
